import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a results list with load more pagination
 */
export default class ResultsListLoadMore extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {string | null} */
  #imageRatioSetting = null;

  /** @type {boolean} */
  #isLoading = false;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();
    this.#setupLoadMoreButton();
    this.#setupGridLayout();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  /**
   * Setup the load more button functionality
   */
  #setupLoadMoreButton() {
    const { loadMoreButton } = this.refs;
    if (!loadMoreButton) return;
    if (loadMoreButton instanceof Element) {
    loadMoreButton.addEventListener('click', this.#handleLoadMoreClick);
    }
  }

  /**
   * Setup grid layout functionality
   */
  #setupGridLayout() {
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Handle load more button click
   */
  #handleLoadMoreClick = async (event) => {
    event.preventDefault();

    if (this.#isLoading) return;

    const { loadMoreButton, grid } = this.refs;
    if (!loadMoreButton || !grid) return;

    const currentPage = parseInt(loadMoreButton.dataset.currentPage) || 1;
    const totalPages = parseInt(loadMoreButton.dataset.totalPages) || 1;

    if (currentPage >= totalPages) return;

    this.#isLoading = true;
    this.#setButtonLoadingState(true);

    try {
      const nextPage = currentPage + 1;
      const newProducts = await this.#loadNextPage(nextPage);

      if (newProducts && newProducts.length > 0) {
        // Append new products to the grid
        grid.append(...newProducts);

        // Update button state
        loadMoreButton.dataset.currentPage = nextPage.toString();
        grid.dataset.currentPage = nextPage.toString();

        // Hide button if we've reached the last page
        if (nextPage >= totalPages) {
          loadMoreButton.disabled = true;
          loadMoreButton.classList.add('hidden');
        }

        // Process new elements for aspect ratios
        this.#processNewElements();

        // Update URL without page parameter to show all loaded products
        this.#updateURL();
      }
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      this.#isLoading = false;
      this.#setButtonLoadingState(false);
    }
  };

  /**
   * Load the next page of products
   * @param {number} pageNumber - The page number to load
   * @returns {Promise<Element[]>} - Array of product elements
   */
  async #loadNextPage(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());

    // Check if we have this page cached
    if (this.pages.has(pageNumber)) {
      return this.#getGridForPage(pageNumber);
    }

    // Fetch the page content
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
    this.pages.set(pageNumber, pageContent);

    return this.#getGridForPage(pageNumber);
  }

  /**
   * Get product elements for a specific page
   * @param {number} page - The page number
   * @returns {Element[] | undefined} - Array of product elements
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    
    const productElements = gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    return Array.from(productElements);
  }

  /**
   * Set button loading state
   * @param {boolean} isLoading - Whether the button should show loading state
   */
  #setButtonLoadingState(isLoading) {
    const { loadMoreButton } = this.refs;
    if (!loadMoreButton) return;

    if (isLoading) {
      loadMoreButton.classList.add('loading');
      loadMoreButton.disabled = true;
    } else {
      loadMoreButton.classList.remove('loading');
      loadMoreButton.disabled = false;
    }
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   */
  #processNewElements() {
    // Wait for the DOM to update
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return ResultsListLoadMore.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * Update URL to remove page parameter
   */
  #updateURL() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    history.replaceState('', '', url.toString());
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages and resetting button
   */
  // #handleFilterUpdate = () => {
  //   this.pages.clear();
  //   this.#isLoading = false;

  //   // Reset button state
  //   const { loadMoreButton } = this.refs;
  //   if (loadMoreButton) {
  //     loadMoreButton.classList.remove('loading', 'hidden');
  //     loadMoreButton.disabled = false;
  //   }

  //   // Store the current lastPage value to detect when it changes
  //   const currentLastPage = this.refs.grid?.dataset.lastPage;

  //   // We need to wait for the DOM to be updated with the new filtered content
  //   // Using mutation observer to detect when the grid actually updates
  //   const observer = new MutationObserver((mutations) => {
  //     // Check if data-last-page changed
  //     const newLastPage = this.refs.grid?.dataset.lastPage;

  //     if (newLastPage !== currentLastPage) {
  //       observer.disconnect();

  //       // Check if component is still connected
  //       if (!this.isConnected) {
  //         return;
  //       }

  //       // Update button state based on new pagination
  //       this.#updateButtonState();
  //     }
  //   });

  //   // Observe the grid for changes
  //   const { grid } = this.refs;
  //   if (grid) {
  //     observer.observe(grid, {
  //       attributes: true,
  //       attributeFilter: ['data-last-page'],
  //       childList: true, // Also watch for child changes in case the whole grid is replaced
  //     });

  //     // Set a timeout as a fallback in case the mutation never fires
  //     setTimeout(() => {
  //       if (observer) {
  //         observer.disconnect();
  //       }
  //     }, 3000);
  //   }
  // };

/**
 * Handle filter updates by clearing cached pages and resetting button
 */
#handleFilterUpdate = () => {
  this.pages.clear();
  this.#isLoading = false;

  // Reset button state
  const { loadMoreButton } = this.refs;
  if (loadMoreButton) {
    loadMoreButton.classList.remove('loading', 'hidden');
    loadMoreButton.disabled = false;
  }

  // Store the current lastPage value to detect when it changes
  const currentLastPage = this.refs.grid?.dataset.lastPage;

  // We need to wait for the DOM to be updated with the new filtered content
  // Using mutation observer to detect when the grid actually updates
  const observer = new MutationObserver((mutations) => {
    // Check if data-last-page changed
    const newLastPage = this.refs.grid?.dataset.lastPage;

    if (newLastPage !== currentLastPage) {
      observer.disconnect();

      // Check if component is still connected
      if (!this.isConnected) {
        return;
      }

      // Update button state based on new pagination
      this.#updateButtonState();

      // Reattach the "Load More" button click event listener
      this.#setupLoadMoreButton();  // Rebind the click event listener to the button
    }
  });

  // Observe the grid for changes
  const { grid } = this.refs;
  if (grid) {
    observer.observe(grid, {
      attributes: true,
      attributeFilter: ['data-last-page'],
      childList: true, // Also watch for child changes in case the whole grid is replaced
    });

    // Set a timeout as a fallback in case the mutation never fires
    setTimeout(() => {
      if (observer) {
        observer.disconnect();
      }
    }, 3000);
  }
};



  /**
   * Update button state based on current pagination
   */
  #updateButtonState() {
    const { loadMoreButton, grid } = this.refs;
    if (!loadMoreButton || !grid) return;

    const currentPage = parseInt(grid.dataset.currentPage) || 1;
    const totalPages = parseInt(grid.dataset.lastPage) || 1;

    loadMoreButton.dataset.currentPage = currentPage.toString();
    loadMoreButton.dataset.totalPages = totalPages.toString();

    if (currentPage >= totalPages) {
      loadMoreButton.disabled = true;
      loadMoreButton.classList.add('hidden');
    } else {
      loadMoreButton.disabled = false;
      loadMoreButton.classList.remove('hidden');
    }
  }
}

if (!customElements.get('results-list-load-more')) {
  customElements.define('results-list-load-more', ResultsListLoadMore);
}
